import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getCollectionCore, createCollectionCore, updateCollectionCore } from '@gv/core';
import { uploadCompressedImage } from '../lib/imageUpload';
import { ArrowLeft, Plus, X as XIcon } from 'lucide-react';

type Props = {
    user: any;
    collectionId?: string | null;
    onBack: () => void;
    onSaved: () => void;
};

export default function CollectionEditorPage({ user, collectionId, onBack, onSaved }: Props) {
    const editing = !!collectionId;
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [zoom, setZoom] = useState(1);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!editing) return;
        let active = true;
        setLoading(true);
        (async () => {
            try {
                const data = await getCollectionCore(supabase, collectionId!);
                if (active && data) {
                    setName(data.name || '');
                    setDescription(data.message_context || '');
                    setImageUrl(data.image_url || null);
                }
            } catch (e) {
                console.error('Failed to load collection', e);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [collectionId, editing]);

    const onPickImage = async (file: File) => {
        setUploading(true);
        try {
            const url = await uploadCompressedImage(file, 'collection-images');
            setImageUrl(url);
            setZoom(1);
        } catch (e) {
            console.error(e);
            alert('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            alert('Name is required');
            return;
        }
        setSaving(true);
        try {
            if (!editing) {
                await createCollectionCore(supabase, user.id, {
                    name: name.trim(),
                    color: '#3b82f6',
                    message_context: description.trim() || null,
                    image_url: imageUrl || null,
                });
            } else {
                await updateCollectionCore(supabase, collectionId!, {
                    name: name.trim(),
                    message_context: description.trim() || null,
                    image_url: imageUrl || null,
                });
            }
            onSaved();
        } catch (e) {
            console.error('Failed to save collection', e);
            alert('Failed to save collection');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 lg:p-8 text-white">
            <div className="max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-3">
                    <button onClick={onBack} className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700" aria-label="Back">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-xl md:text-2xl font-light tracking-wide flex-1 text-center">{editing ? 'Edit Collection' : 'New Collection'}</h2>
                    <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-white text-black font-medium">
                        {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
                    </button>
                </div>

                {/* Stacked editor: Name + Image above, Description below */}
                <div className="space-y-4 sm:space-y-6">
                    {/* Name */}
                    <div className="border border-gray-800 p-3 sm:p-4 bg-black">
                        <label className="block text-xs text-gray-400 mb-1">Name</label>
                        <input value={name} onChange={e => setName(e.target.value)} placeholder="Collection name" className="w-full bg-gray-900 border border-gray-800 px-3 py-2" />
                    </div>

                    {/* Image */}
                    <div className="border border-gray-800 p-3 sm:p-4 bg-black max-w-md mx-auto w-full">
                        <div className="mb-4">
                            <label className="block relative group w-full aspect-[4/3] max-w-xl mx-auto bg-gray-800 rounded-lg overflow-hidden cursor-pointer" title={imageUrl ? 'Replace image' : 'Set image'}>
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) onPickImage(f);
                                }} />
                                {imageUrl ? (
                                    <img src={imageUrl} alt="Collection" className="w-full h-full object-contain will-change-transform transition-transform duration-200" style={{ transform: `scale(${zoom})` }} />
                                ) : (
                                    <div className="w-full h-full" />
                                )}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 border border-gray-600 opacity-100 sm:opacity-70 group-hover:opacity-100 transition-opacity">
                                        {uploading ? (<span className="text-xs text-white">Uploading…</span>) : (imageUrl ? <XIcon className="w-5 h-5 text-white" /> : <Plus className="w-5 h-5 text-white" />)}
                                    </div>
                                </div>
                            </label>
                            {imageUrl && (
                                <div className="mt-2 flex items-center gap-3">
                                    <label className="text-xs text-gray-400">Zoom</label>
                                    <input type="range" min={0.5} max={2} step={0.01} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="flex-1" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    <div className="border border-gray-800 p-3 sm:p-4 bg-black">
                        <label className="block text-xs text-gray-400 mb-1">Description</label>
                        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={8} className="w-full bg-gray-900 border border-gray-800 px-3 py-2" placeholder="What’s this collection?" />
                    </div>
                </div>
            </div>
        </div>
    );
}
